// Rôles d'un utilisateur : attribution, retrait, validation d'une attribution en attente
// (règle des quatre yeux, D-022).
import { Bandeau, Bouton, ConfirmationIntegree, PuceEtat, Selection } from '@eec/ui';
import { useState } from 'react';
import { requete } from '../../api/client';
import type { Role, Utilisateur } from '../../api/types';
import { useAction } from '../../commun/useAction';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import commun from '../../commun/commun.module.css';

export function GestionRoles({ utilisateur, roles }: { utilisateur: Utilisateur; roles: Role[] }) {
  const { contexte } = useSession();
  const [roleChoisi, setRoleChoisi] = useState('');
  const administrer = peut(contexte, 'utilisateurs', 'administrer');
  const valider = peut(contexte, 'utilisateurs', 'valider');
  const moi = contexte?.compte.id === utilisateur.compte_id;
  const invalider = [['utilisateurs'], ['contexte']] as const;

  const attribution = useAction({
    executer: (roleId: string) =>
      requete<{ statut: string; code: string }>(`/appartenances/${utilisateur.appartenance_id}/roles`, { methode: 'POST', corps: { role_id: roleId } }),
    succes: (r) =>
      r.statut === 'active'
        ? `Rôle ${r.code} attribué.`
        : `Rôle ${r.code} enregistré en attente de validation : une autre personne détenant le droit de valider doit le confirmer.`,
    invalider,
    apres: () => setRoleChoisi(''),
  });
  const retrait = useAction({
    executer: (roleId: string) => requete(`/appartenances/${utilisateur.appartenance_id}/roles/${roleId}`, { methode: 'DELETE' }),
    succes: 'Rôle retiré.',
    invalider,
  });
  const validation = useAction({
    executer: (roleId: string) => requete(`/appartenances/${utilisateur.appartenance_id}/roles/${roleId}/valider`, { methode: 'POST' }),
    succes: 'Attribution validée : le rôle est désormais actif.',
    invalider,
  });

  const disponibles = roles.filter((r) => !utilisateur.roles.some((x) => x.role_id === r.id));
  const erreur = attribution.messageErreur ?? retrait.messageErreur ?? validation.messageErreur;

  return (
    <div className={commun.pile}>
      {erreur ? <Bandeau type="erreur">{erreur}</Bandeau> : null}
      {utilisateur.roles.length === 0 ? <p className={commun.na}>Aucun rôle attribué.</p> : null}
      <ul className={commun.pile} style={{ listStyle: 'none', padding: 0, margin: 0 }} aria-label="Rôles attribués">
        {utilisateur.roles.map((r) => (
          <li key={r.role_id} className={commun.ligne} style={{ justifyContent: 'space-between' }}>
            <span>
              <strong>{r.libelle}</strong> <span className={commun.na}>({r.code})</span>{' '}
              {r.statut === 'active' ? (
                <PuceEtat ton="ok" libelle="Actif" />
              ) : (
                <PuceEtat ton="attente" libelle="En attente de validation" />
              )}{' '}
              {r.sensible ? <PuceEtat ton="neutre" libelle="Sensible" /> : null}
            </span>
            <span className={commun.ligne}>
              {r.statut === 'en_attente_validation' && valider ? (
                moi ? (
                  <span className={commun.na}>Validation par une autre personne</span>
                ) : (
                  <Bouton petit chargement={validation.enCours} onClick={() => validation.lancer(r.role_id)}>
                    Valider l&apos;attribution
                  </Bouton>
                )
              ) : null}
              {administrer ? (
                <ConfirmationIntegree
                  petit
                  libelle={`Retirer ${r.libelle}`}
                  question={`Retirer le rôle ${r.libelle} ? L'effet est immédiat sur les droits de la personne.`}
                  libelleConfirmer="Confirmer le retrait"
                  enCours={retrait.enCours}
                  onConfirmer={() => retrait.lancer(r.role_id)}
                />
              ) : null}
            </span>
          </li>
        ))}
      </ul>
      {administrer && utilisateur.statut_appartenance !== 'revoquee' ? (
        <form
          className={commun.ligne}
          style={{ alignItems: 'flex-end' }}
          onSubmit={(e) => {
            e.preventDefault();
            if (roleChoisi) attribution.lancer(roleChoisi);
          }}
        >
          <Selection
            libelle="Attribuer un rôle"
            optionVide="Choisir un rôle"
            value={roleChoisi}
            onChange={(e) => setRoleChoisi(e.target.value)}
            options={disponibles.map((r) => ({ valeur: r.id, libelle: `${r.libelle}${r.sensible ? ' (sensible)' : ''}` }))}
            aide={moi ? 'Un rôle sensible que vous vous attribuez reste en attente jusqu\'à sa validation par une autre personne.' : undefined}
          />
          <Bouton type="submit" disabled={!roleChoisi} chargement={attribution.enCours}>
            Attribuer
          </Bouton>
        </form>
      ) : null}
    </div>
  );
}
